import { PaymentMode } from '../../lib/paymentApi';
import { Link } from 'react-router-dom';
import { CheckoutStateKind, PaymentCheckoutState } from '../../hooks/usePaymentCheckout';
import { POINTS_PER_RUPEE } from '../../lib/constants';
import { money } from '../../lib/format';
import { CommerceConfig, Quote } from '../../types/commerce';
import PriceBreakdown from '../PriceBreakdown/PriceBreakdown';

interface Props {
  readonly config: CommerceConfig | null;
  readonly quote: Quote | null;
  readonly quoteBusy: boolean;
  readonly quoteError: string;
  readonly points: number;
  readonly onPointsChange: (points: number) => void;
  readonly paymentState: PaymentCheckoutState;
  readonly paymentBlocked: boolean;
  readonly onCheckStatus: () => void;
  readonly onReleaseAndRetry: () => void;
  /** Payment stays disabled while other checkout details are still being edited. */
  readonly canPay: boolean;
  readonly onPay: () => void;
}

export default function OrderSummary({
  config,
  quote,
  quoteBusy,
  quoteError,
  points,
  onPointsChange,
  paymentState,
  paymentBlocked,
  onCheckStatus,
  onReleaseAndRetry,
  canPay,
  onPay,
}: Props) {
  return (
    <aside className="checkout-summary">
      <h2>Order summary</h2>
      {config && !config.enabled && <p>Checkout is not currently enabled.</p>}
      {config?.payment_mode === PaymentMode.Test && <p>Sandbox payment — no real charge.</p>}
      {quoteBusy && <p role="status">Updating total…</p>}
      {quoteError && <p role="alert">{quoteError}</p>}
      {quote && (
        <>
          <PriceBreakdown
            rows={[
              ['Original product subtotal', quote.original_paise],
              ['Product discount', -quote.discount_paise],
              ['Product charge', quote.product_paise],
              ['Transportation', quote.shipping_paise],
              ['Reward points used', -quote.points_value_paise],
              ['Amount payable', quote.amount_paise],
            ]}
          />
          <p>Delivered by {quote.delivery.latest}</p>
          <p>
            {quote.available_points} points available · {POINTS_PER_RUPEE} points = ₹1
          </p>
          <p>Earn {quote.purchase_points} points on delivery.</p>
        </>
      )}
      <label>
        Points to use
        <input
          type="number"
          min="0"
          step="1"
          disabled={paymentBlocked}
          value={points}
          onChange={(e) => onPointsChange(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
        />
      </label>
      {quote && (
        <p>
          Maximum {quote.maximum_points} points; applied {quote.applied_points}.
        </p>
      )}
      {paymentState.kind === CheckoutStateKind.Pending && (
        <div role="status">
          <p>{paymentState.message}</p>
          <button onClick={onCheckStatus}>Check payment status</button>
          <button onClick={onReleaseAndRetry}>Release reservation and retry</button>
          <p>A payment already processing may still complete.</p>
        </div>
      )}
      {paymentState.kind === CheckoutStateKind.Error && <p role="alert">{paymentState.message}</p>}
      {paymentState.kind === CheckoutStateKind.Success && (
        <p role="status">
          Payment confirmed. <Link to="/orders">View orders</Link>
        </p>
      )}
      <button
        className="checkout-pay"
        disabled={!quote || quoteBusy || paymentBlocked || !canPay}
        onClick={onPay}
      >
        {paymentBlocked
          ? 'Confirming payment…'
          : quote
            ? `Pay ${money(quote.amount_paise)}`
            : 'Complete checkout details'}
      </button>
    </aside>
  );
}
